/**
 * QdsTreeView Web Component
 *
 * A tree view component for displaying hierarchical file/folder structures.
 * Uses ITreeDataProvider for data operations, making it storage-backend agnostic.
 *
 * @element qds-tree-view
 *
 * @attr {String} width - Initial width of the tree view (default: 300px)
 * @attr {Boolean} resizable - Whether the tree view can be resized (default: false)
 *
 * @csspart container - The main container
 * @csspart tree - The tree list container
 * @csspart node - A tree node element
 * @csspart folder - A folder node
 * @csspart file - A file node
 *
 * @fires file-selected - When a file is selected { detail: { node: TreeNode } }
 * @fires folder-expanded - When a folder is expanded { detail: { node: TreeNode } }
 * @fires folder-collapsed - When a folder is collapsed { detail: { node: TreeNode } }
 * @fires node-selected - When any node is selected { detail: { node: TreeNode } }
 * @fires node-contextmenu - When a node is right-clicked { detail: { node: TreeNode, mouseX: number, mouseY: number } }
 */

import type { ITreeDataProvider } from '../providers/ITreeDataProvider.js';
import type { TreeNode } from '../../types/index.js';
import { loadBaseCSS } from '../shared/button-utils.js';

loadBaseCSS();

interface FileSelectedEventDetail {
  node: TreeNode;
}

interface FolderEventDetail {
  node: TreeNode;
}

class QdsTreeView extends HTMLElement {
  private _provider: ITreeDataProvider | null;
  private _selectedNode: TreeNode | null;
  private _expandedNodes: Set<string>;
  private _rootNodes: TreeNode[];
  private _loading: boolean;
  private _width: string;
  private _resizable: boolean;
  private _boundKeydownHandler: EventListener;

  constructor() {
    super();

    this._provider = null;
    this._selectedNode = null;
    this._expandedNodes = new Set();
    this._rootNodes = [];
    this._loading = false;
    this._width = '300px';
    this._resizable = false;
    this._boundKeydownHandler = this._handleKeydown.bind(this) as EventListener;

    // Create shadow DOM
    this.attachShadow({ mode: 'open' });

    // Initial render
    this._render();
  }

  static get observedAttributes(): string[] {
    return ['width', 'resizable'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'width':
        this._width = newValue || '300px';
        this._updateWidth();
        break;
      case 'resizable':
        this._resizable = newValue === 'true' || newValue === '';
        this._render();
        break;
    }
  }

  connectedCallback(): void {
    if (!this.isConnected) return;

    // Add keyboard navigation
    if (this.shadowRoot) {
      this.shadowRoot.addEventListener('keydown', this._boundKeydownHandler);
    }
  }

  disconnectedCallback(): void {
    // Cleanup
    if (this.shadowRoot) {
      this.shadowRoot.removeEventListener('keydown', this._boundKeydownHandler);
    }
  }

  /**
   * Set the data provider
   */
  set dataProvider(provider: ITreeDataProvider) {
    this._provider = provider;

    // Subscribe to provider events
    this._provider.on('created', this._handleProviderChange.bind(this));
    this._provider.on('deleted', this._handleProviderChange.bind(this));
    this._provider.on('changed', this._handleProviderChange.bind(this));

    // Load initial data
    this._initialize();
  }

  get dataProvider(): ITreeDataProvider | null {
    return this._provider;
  }

  /**
   * Get the selected node
   */
  get selectedNode(): TreeNode | null {
    return this._selectedNode;
  }

  /**
   * Initialize the tree view by loading root nodes
   */
  private async _initialize(): Promise<void> {
    console.log('[qds-tree-view] _initialize called, provider:', this._provider);
    if (!this._provider) {
      console.warn('[qds-tree-view] No provider set, cannot initialize');
      return;
    }

    // Save the ID of the currently selected node
    const selectedNodeId = this._selectedNode?.id;

    this._loading = true;
    this._updateLoadingState();

    try {
      console.log('[qds-tree-view] Calling provider.getRootNodes()...');
      this._rootNodes = await this._provider.getRootNodes();
      console.log('[qds-tree-view] Loaded root nodes:', this._rootNodes.length);
      this._loading = false;

      // Restore selection if we had a selected node
      if (selectedNodeId) {
        const node = this._findNode(selectedNodeId);
        if (node) {
          this._selectedNode = node;
          node.selected = true;
        } else {
          // Node no longer exists (was deleted), clear selection
          this._selectedNode = null;
        }
      }

      this._render();
    } catch (error) {
      console.error('[qds-tree-view] Error loading tree data:', error);
      this._loading = false;
      this._render();
    }
  }

  /**
   * Handle provider data changes
   */
  private _handleProviderChange(): void {
    this._initialize();
  }

  /**
   * Toggle node expansion
   */
  private async _toggleNode(node: TreeNode): Promise<void> {
    if (node.type !== 'folder') return;

    const isExpanded = this._expandedNodes.has(node.id);

    if (isExpanded) {
      // Collapse
      this._expandedNodes.delete(node.id);
      node.expanded = false;

      this.dispatchEvent(new CustomEvent<FolderEventDetail>('folder-collapsed', {
        detail: { node },
        bubbles: true,
        composed: true
      }));
    } else {
      // Expand
      this._expandedNodes.add(node.id);
      node.expanded = true;

      // Load children if not loaded
      if (!node.childrenLoaded && this._provider) {
        try {
          const children = await this._provider.getChildren(node.id);
          node.items = children;
          node.childrenLoaded = true;
        } catch (error) {
          console.error('Error loading children:', error);
        }
      }

      this.dispatchEvent(new CustomEvent<FolderEventDetail>('folder-expanded', {
        detail: { node },
        bubbles: true,
        composed: true
      }));
    }

    this._render();
  }

  /**
   * Select a node
   */
  private _selectNode(node: TreeNode): void {
    // Update selection state
    if (this._selectedNode) {
      this._selectedNode.selected = false;
    }

    this._selectedNode = node;
    node.selected = true;

    // Emit events
    this.dispatchEvent(new CustomEvent<FileSelectedEventDetail>('node-selected', {
      detail: { node },
      bubbles: true,
      composed: true
    }));

    if (node.type === 'file') {
      this.dispatchEvent(new CustomEvent<FileSelectedEventDetail>('file-selected', {
        detail: { node },
        bubbles: true,
        composed: true
      }));
    }

    this._render();
  }

  /**
   * Handle node click
   */
  private _handleNodeClick(event: Event): void {
    const target = event.target as HTMLElement;
    const nodeElement = target.closest('[data-node-id]') as HTMLElement;

    if (!nodeElement) return;

    const nodeId = nodeElement.getAttribute('data-node-id');
    if (!nodeId) return;

    const node = this._findNode(nodeId);
    if (!node) return;

    // If clicking on expand/collapse icon
    if (target.closest('.node-icon')) {
      if (node.type === 'folder') {
        this._toggleNode(node);
      }
      return;
    }

    // Select the node
    this._selectNode(node);

    // If it's a folder, also toggle it
    if (node.type === 'folder') {
      this._toggleNode(node);
    }
  }

  /**
   * Find a node by ID in the tree
   */
  private _findNode(nodeId: string, nodes?: TreeNode[]): TreeNode | null {
    const searchNodes = nodes || this._rootNodes;

    for (const node of searchNodes) {
      if (node.id === nodeId) {
        return node;
      }

      if (node.items) {
        const found = this._findNode(nodeId, node.items);
        if (found) return found;
      }
    }

    return null;
  }

  /**
   * Handle keyboard navigation
   */
  private _handleKeydown(event: KeyboardEvent): void {
    if (!this._selectedNode) return;

    switch (event.key) {
      case 'ArrowRight':
        // Expand folder or move to first child
        if (this._selectedNode.type === 'folder') {
          if (!this._expandedNodes.has(this._selectedNode.id)) {
            this._toggleNode(this._selectedNode);
          } else if (this._selectedNode.items && this._selectedNode.items.length > 0) {
            this._selectNode(this._selectedNode.items[0]);
          }
        }
        event.preventDefault();
        break;

      case 'ArrowLeft':
        // Collapse folder or move to parent
        if (this._selectedNode.type === 'folder' && this._expandedNodes.has(this._selectedNode.id)) {
          this._toggleNode(this._selectedNode);
        } else if (this._selectedNode.parentId) {
          const parent = this._findNode(this._selectedNode.parentId);
          if (parent) {
            this._selectNode(parent);
          }
        }
        event.preventDefault();
        break;

      case 'ArrowDown':
        // Move to next visible node
        this._selectNextNode();
        event.preventDefault();
        break;

      case 'ArrowUp':
        // Move to previous visible node
        this._selectPreviousNode();
        event.preventDefault();
        break;

      case 'Enter':
      case ' ':
        // Toggle folder or trigger file selection
        if (this._selectedNode.type === 'folder') {
          this._toggleNode(this._selectedNode);
        } else {
          this.dispatchEvent(new CustomEvent<FileSelectedEventDetail>('file-selected', {
            detail: { node: this._selectedNode },
            bubbles: true,
            composed: true
          }));
        }
        event.preventDefault();
        break;
    }
  }

  /**
   * Select next visible node
   */
  private _selectNextNode(): void {
    const visibleNodes = this._getVisibleNodes();
    if (!this._selectedNode || visibleNodes.length === 0) return;

    const currentIndex = visibleNodes.findIndex(n => n.id === this._selectedNode!.id);
    if (currentIndex < visibleNodes.length - 1) {
      this._selectNode(visibleNodes[currentIndex + 1]);
    }
  }

  /**
   * Select previous visible node
   */
  private _selectPreviousNode(): void {
    const visibleNodes = this._getVisibleNodes();
    if (!this._selectedNode || visibleNodes.length === 0) return;

    const currentIndex = visibleNodes.findIndex(n => n.id === this._selectedNode!.id);
    if (currentIndex > 0) {
      this._selectNode(visibleNodes[currentIndex - 1]);
    }
  }

  /**
   * Get all visible nodes (flattened, respecting expand/collapse state)
   */
  private _getVisibleNodes(): TreeNode[] {
    const visible: TreeNode[] = [];

    const traverse = (nodes: TreeNode[]) => {
      for (const node of nodes) {
        visible.push(node);

        if (node.type === 'folder' && this._expandedNodes.has(node.id) && node.items) {
          traverse(node.items);
        }
      }
    };

    traverse(this._rootNodes);
    return visible;
  }

  /**
   * Update width
   */
  private _updateWidth(): void {
    const container = this.shadowRoot?.querySelector('.tree-container') as HTMLElement;
    if (container) {
      container.style.width = this._width;
    }
  }

  /**
   * Update loading state
   */
  private _updateLoadingState(): void {
    const loadingEl = this.shadowRoot?.querySelector('.loading') as HTMLElement;
    const treeEl = this.shadowRoot?.querySelector('.tree') as HTMLElement;

    if (loadingEl && treeEl) {
      loadingEl.style.display = this._loading ? 'flex' : 'none';
      treeEl.style.display = this._loading ? 'none' : 'block';
    }
  }

  /**
   * Create HTML for tree nodes
   */
  private _createTreeHTML(nodes: TreeNode[], level: number = 0): string {
    if (!nodes || nodes.length === 0) {
      if (level === 0) {
        return '<div class="empty-state">No files or folders</div>';
      }
      return '';
    }

    return nodes.map(node => {
      const isExpanded = this._expandedNodes.has(node.id);
      const isSelected = node.id === this._selectedNode?.id;
      const hasChildren = node.type === 'folder' && node.items && node.items.length > 0;

      const nodeClasses = [
        'tree-node',
        node.type === 'folder' ? 'folder-node' : 'file-node',
        isSelected ? 'selected' : '',
        isExpanded ? 'expanded' : ''
      ].filter(Boolean).join(' ');

      const iconClass = node.type === 'folder'
        ? (isExpanded ? 'folder-open-icon' : 'folder-icon')
        : 'file-icon';

      const expandIcon = node.type === 'folder'
        ? (isExpanded ? '▼' : '▶')
        : '';

      const indentStyle = `--tree-node-indent: calc(${level} * var(--_tree-node-indent-step))`;

      return `
        <div class="${nodeClasses}"
             data-node-id="${node.id}"
             data-node-type="${node.type}"
             style="${indentStyle}"
             part="node ${node.type === 'folder' ? 'folder' : 'file'}"
             role="treeitem"
             aria-expanded="${node.type === 'folder' ? isExpanded : undefined}"
             aria-selected="${isSelected}"
             tabindex="${isSelected ? 0 : -1}">
          <span class="node-icon ${iconClass}">
            ${expandIcon}
          </span>
          <span class="node-label">
            ${node.name}
          </span>
        </div>
        ${hasChildren && isExpanded ? `
          <div class="node-children" role="group">
            ${this._createTreeHTML(node.items!, level + 1)}
          </div>
        ` : ''}
      `;
    }).join('');
  }

  /**
   * Render the component
   */
  private _render(): void {
    if (!this.shadowRoot) return;

    const resizeHandle = this._resizable ? `
      <div class="resize-handle" part="resize-handle"></div>
    ` : '';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          --_tree-font-family: var(--qds-tree-font-family, var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif)));
          --_tree-font-size: var(--qds-tree-font-size, var(--theme-font-size-sm, 14px));
          --_tree-border-color: var(--qds-tree-border-color, var(--theme-border-primary));
          --_tree-border-strong-color: var(--qds-tree-border-strong-color, var(--theme-border-strong));
          --_tree-surface-color: var(--qds-tree-surface-color, var(--theme-background-primary));
          --_tree-hover-color: var(--qds-tree-hover-color, var(--theme-background-hover));
          --_tree-selected-color: var(--qds-tree-selected-color, var(--theme-background-accent-subtle));
          --_tree-text-color: var(--qds-tree-text-color, var(--theme-text-primary));
          --_tree-subtle-text-color: var(--qds-tree-subtle-text-color, var(--theme-text-secondary));
          --_tree-muted-text-color: var(--qds-tree-muted-text-color, var(--theme-text-tertiary));
          --_tree-accent-color: var(--qds-tree-accent-color, var(--theme-accent-primary));
          --_tree-row-padding-block: var(--qds-tree-row-padding-block, var(--theme-space-2, 8px));
          --_tree-row-padding-inline: var(--qds-tree-row-padding-inline, var(--theme-space-3, 12px));
          --_tree-node-gap: var(--qds-tree-node-gap, var(--theme-space-2, 8px));
          --_tree-node-indent-step: var(--qds-tree-node-indent-step, var(--theme-space-5, 20px));
          --_tree-node-radius: var(--qds-tree-node-radius, var(--theme-radius-sm, 4px));
          --_tree-spinner-size: var(--qds-tree-spinner-size, calc(var(--theme-font-size-md, 16px) + var(--theme-space-1, 4px)));
          --_tree-icon-size: var(--qds-tree-icon-size, calc(var(--theme-font-size-md, 16px) + var(--theme-space-1, 4px)));
          --_tree-scrollbar-size: var(--qds-tree-scrollbar-size, var(--theme-space-2, 8px));
          --_tree-resize-handle-width: var(--qds-tree-resize-handle-width, var(--theme-space-1, 4px));
          display: block;
          font-family: var(--_tree-font-family);
          font-size: var(--_tree-font-size);
          color: var(--_tree-text-color);
          position: relative;
        }

        *, *::before, *::after {
          box-sizing: border-box;
        }

        .tree-container {
          width: ${this._width};
          height: 100%;
          background: var(--_tree-surface-color);
          border-right: 1px solid var(--_tree-border-color);
          overflow-y: auto;
          overflow-x: hidden;
          position: relative;
          scrollbar-width: thin;
          scrollbar-color: var(--_tree-border-strong-color) transparent;
        }

        .loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--theme-space-2, 8px);
          padding: var(--theme-space-6, 24px) var(--theme-space-5, 20px);
          color: var(--_tree-subtle-text-color);
        }

        .spinner {
          width: var(--_tree-spinner-size);
          height: var(--_tree-spinner-size);
          border: 2px solid var(--_tree-border-color);
          border-top-color: var(--_tree-accent-color);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .tree {
          padding: var(--theme-space-2, 8px) 0;
        }

        .empty-state {
          padding: var(--theme-space-6, 24px) var(--theme-space-5, 20px);
          text-align: center;
          color: var(--_tree-subtle-text-color);
          font-style: italic;
        }

        .tree-node {
          display: flex;
          align-items: center;
          min-height: 36px;
          gap: var(--_tree-node-gap);
          padding: var(--_tree-row-padding-block) var(--_tree-row-padding-inline);
          padding-left: calc(var(--_tree-row-padding-inline) + var(--tree-node-indent, 0px));
          cursor: pointer;
          user-select: none;
          transition: background-color var(--transition-speed-fast, 0.15s) ease, color var(--transition-speed-fast, 0.15s) ease;
          position: relative;
          border-radius: var(--_tree-node-radius);
          color: var(--_tree-text-color);
        }

        .tree-node:hover {
          background: var(--_tree-hover-color);
        }

        .tree-node.selected {
          background: var(--_tree-selected-color);
          color: var(--_tree-accent-color);
          font-weight: var(--theme-font-weight-medium, 500);
        }

        .tree-node:focus-visible {
          outline: 2px solid var(--_tree-accent-color);
          outline-offset: -2px;
        }

        .node-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: var(--_tree-icon-size);
          height: var(--_tree-icon-size);
          min-width: var(--_tree-icon-size);
          font-size: var(--theme-font-size-xs, 12px);
          color: var(--_tree-subtle-text-color);
          flex-shrink: 0;
        }

        .folder-icon,
        .folder-open-icon {
          color: var(--_tree-accent-color);
        }

        .node-label {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .node-children {
          /* Children are indented via style attribute on each node */
        }

        .resize-handle {
          position: absolute;
          top: 0;
          right: 0;
          width: var(--_tree-resize-handle-width);
          height: 100%;
          background: transparent;
          cursor: col-resize;
          z-index: 10;
        }

        .resize-handle:hover {
          background: var(--_tree-accent-color);
        }

        /* Scrollbar styling */
        .tree-container::-webkit-scrollbar {
          width: var(--_tree-scrollbar-size);
        }

        .tree-container::-webkit-scrollbar-track {
          background: transparent;
        }

        .tree-container::-webkit-scrollbar-thumb {
          background-color: var(--_tree-border-strong-color);
          border-radius: var(--theme-radius-sm, 4px);
          border: 2px solid transparent;
        }

        .tree-container::-webkit-scrollbar-thumb:hover {
          background-color: var(--_tree-muted-text-color);
        }
      </style>

      <div class="tree-container" part="container">
        <div class="loading">
          <div class="spinner"></div>
          <span>Loading...</span>
        </div>
        <div class="tree" part="tree" role="tree">
          ${this._createTreeHTML(this._rootNodes)}
        </div>
        ${resizeHandle}
      </div>
    `;

    // Update loading state
    this._updateLoadingState();

    // Attach event listeners
    const tree = this.shadowRoot.querySelector('.tree');
    if (tree) {
      tree.addEventListener('click', this._handleNodeClick.bind(this));
      tree.addEventListener('contextmenu', this._handleContextMenu.bind(this));
    }

    // Attach resize handle listeners if resizable
    if (this._resizable) {
      const resizeHandle = this.shadowRoot.querySelector('.resize-handle') as HTMLElement;
      if (resizeHandle) {
        resizeHandle.addEventListener('mousedown', this._handleResizeStart.bind(this));
      }
    }
  }

  /**
   * Handle resize start
   */
  private _handleResizeStart(event: MouseEvent): void {
    event.preventDefault();

    const startX = event.clientX;
    const container = this.shadowRoot?.querySelector('.tree-container') as HTMLElement;
    const startWidth = container.offsetWidth;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(200, Math.min(600, startWidth + deltaX));
      this._width = `${newWidth}px`;
      this._updateWidth();
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }

  /**
   * Handle context menu (right-click) event
   * Emits node-contextmenu event for parent component to handle
   */
  private _handleContextMenu(event: Event): void {
    event.preventDefault();
    const mouseEvent = event as MouseEvent;

    // Find which node was right-clicked
    const target = event.target as HTMLElement;
    const nodeElement = target.closest('[data-node-id]') as HTMLElement;

    let node: TreeNode | null = null;

    if (nodeElement) {
      const nodeId = nodeElement.getAttribute('data-node-id');
      if (nodeId) {
        node = this._findNode(nodeId);
      }
    }

    // Emit event for parent component to handle
    this.dispatchEvent(new CustomEvent('node-contextmenu', {
      detail: {
        node: node,
        mouseX: mouseEvent.clientX,
        mouseY: mouseEvent.clientY
      },
      bubbles: true,
      composed: true
    }));
  }
}

// Register the component
if (!customElements.get('qds-tree-view')) {
  customElements.define('qds-tree-view', QdsTreeView);
}

export { QdsTreeView };
